import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Keeps a list's page and page size in the URL (?page=10&per_page=20), so the browser Back button and
// "Kembali" buttons on detail pages return to the same page instead of page 1.
const STORAGE_PREFIX = 'list-search:';

const readPositiveInt = (search, key, fallback) => {
    const value = Number.parseInt(new URLSearchParams(search || '').get(key) || '', 10);
    return Number.isFinite(value) && value > 0 ? value : fallback;
};

// Last known query string of a list page, for detail pages that link back to it.
export const listUrl = (path) => {
    try {
        return `${path}${sessionStorage.getItem(`${STORAGE_PREFIX}${path}`) || ''}`;
    } catch (e) {
        return path;
    }
};

// Keeps a list's search FILTERS (district, status, keyword, dates, …) in the URL, so the browser Back
// button and "Kembali" buttons on detail/edit pages return to the same filtered view instead of the
// unfiltered list. Pair it with useUrlPagination (which owns ?page/?per_page). `defaults` is the empty
// filter shape; `paramMap` maps a filter key to its URL param name (defaults to the key itself).
// Returns [filters, commitFilters]: `filters` is read from the URL (so Back/Forward restores it);
// `commitFilters(next)` writes the chosen filters into the URL and resets to the first page.
export const useUrlFilters = (defaults, paramMap = {}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const paramFor = (key) => paramMap[key] || key;

    const readFromUrl = () => {
        const p = new URLSearchParams(location.search || '');
        const out = { ...defaults };
        Object.keys(defaults).forEach((key) => {
            const value = p.get(paramFor(key));
            if (value !== null && value !== '') {
                out[key] = value;
            }
        });
        return out;
    };

    const [filters, setFiltersState] = useState(readFromUrl);

    // URL -> state: browser Back/Forward, and returning from a detail/edit page, restore the filters.
    useEffect(() => {
        setFiltersState(readFromUrl());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const commitFilters = (next) => {
        const params = new URLSearchParams(location.search || '');
        Object.keys(defaults).forEach((key) => {
            const value = next ? next[key] : undefined;
            const name = paramFor(key);
            if (value !== undefined && value !== null && String(value) !== '' && String(value) !== String(defaults[key] ?? '')) {
                params.set(name, String(value));
            } else {
                params.delete(name);
            }
        });
        params.delete('page'); // a new search starts on the first page
        const qs = params.toString();
        navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    };

    return [filters, commitFilters];
};

// Keeps a single committed filter value (a tab/dropdown choice such as case type or record scope) in the
// URL, so Back/"Kembali" from a detail page restores it. Returns [value, commit]: `value` is read from
// the URL (default when absent); `commit(next)` writes it and resets to the first page.
export const useUrlState = (name, defaultValue = '') => {
    const location = useLocation();
    const navigate = useNavigate();

    const read = () => {
        const value = new URLSearchParams(location.search || '').get(name);
        return value === null ? defaultValue : value;
    };

    const [value, setValue] = useState(read);

    useEffect(() => {
        setValue(read());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const commit = (next) => {
        const params = new URLSearchParams(location.search || '');
        if (next !== undefined && next !== null && String(next) !== '' && String(next) !== String(defaultValue ?? '')) {
            params.set(name, String(next));
        } else {
            params.delete(name);
        }
        params.delete('page');
        const qs = params.toString();
        navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    };

    return [value, commit];
};

const useUrlPagination = (defaultPerPage = 10) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [page, setPage] = useState(() => readPositiveInt(location.search, 'page', 1));
    const [perPage, setPerPage] = useState(() => readPositiveInt(location.search, 'per_page', defaultPerPage));

    // State -> URL (replace, so paging does not add history entries).
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (page > 1) params.set('page', String(page)); else params.delete('page');
        if (perPage !== defaultPerPage) params.set('per_page', String(perPage)); else params.delete('per_page');
        const next = params.toString();
        const search = next ? `?${next}` : '';
        if (next !== location.search.replace(/^\?/, '')) {
            navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, state: location.state });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage]);

    // Snapshot the full current URL (page + filters) so listUrl() and "Kembali" links return to the exact
    // filtered page instead of page 1 / the unfiltered list.
    useEffect(() => {
        try {
            sessionStorage.setItem(`${STORAGE_PREFIX}${location.pathname}`, location.search || '');
        } catch (e) {
            // Storage unavailable: the URL still carries the state.
        }
    }, [location.pathname, location.search]);

    // URL -> state (browser Back/Forward between list URLs).
    useEffect(() => {
        const urlPage = readPositiveInt(location.search, 'page', 1);
        const urlPerPage = readPositiveInt(location.search, 'per_page', defaultPerPage);
        if (urlPage !== page) setPage(urlPage);
        if (urlPerPage !== perPage) setPerPage(urlPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    return { page, setPage, perPage, setPerPage };
};

export default useUrlPagination;
